import {AddressDetailRes, AddressReq} from "./address.contract";
import {BankAccountRes, CourtOfficeDetailsRes, TaxOfficeDetailsRes} from "./bank.contract";
import {Type} from 'class-transformer';
import {ProductDetailsRes} from './product.contract';
import moment from "moment";
import {UserDetailsMinRes, UserDetailsRes} from './user.contract';
import {StandardFormOfAccounts, TypeOfProfitDetermination} from '../../../enums/fibu.enums';
import {InvoiceDocumentFormat} from '../../../enums/invoice-document-format';
import {
    FileUploadRes,
    FilterType,
    YASC_CRUD_ALL_MODES_V2,
    YASC_CRUD_PERSISTED_MODES_V2,
    yascClassDefaultSectionModes,
    yascClassFormSectionTitle,
    yascCollection,
    yascDisplayName,
    yascEmail,
    yascEntityName,
    yascFormTooltip,
    yascMaxLength,
    yascMinLength,
    yascNested,
    yascPattern,
    yascPhone,
    yascRequired,
    yascTableIgnore,
    yascTableIndex,
    yascTableWidth,
    yascUrl,
} from "@yasc/yasc-material-components";
import {
    ContactPersonDetailRes,
    ContactPersonUpdateReq,
    NoteDetailRes,
    OpeningTimeDetailRes,
    TrackerDetailRes
} from '../crm';
import {
    yascClassDisplayName,
    yascFormGrid,
    yascFormIgnore, yascFormKind, yascFormPlaceholder, yascFormRows, yascTableFilterType
} from '@yasc/yasc-material-components';

export class CompanyMinimumRes {
    id!: number;
    customerNumber!: string;
    name!: string;
}

export class CompanyDetailsMinRes {
    public id!: number;
    public name!: string;
    public description!: string;
    public customerNumber!: string;
    public stNr?: string;
    public email: string = "";
    public ustNr?: string;
    public website: string = "";
    public phone: string = "";
    public billingPaymentTermsDays?: number | null;
    public billingPaymentDiscountPercent?: number | null;
    public billingPaymentDiscountDays?: number | null;
    public billingPreferredInvoiceFormat?: InvoiceDocumentFormat | null;
    public billingEdifactInterchangeId?: string | null;
    public billingEdifactCompanyReference?: string | null;
}


@yascEntityName('general_company')
@yascClassDisplayName('general_company')
@yascClassFormSectionTitle('crm_company_basicInfo_tabLabel')
@yascClassDefaultSectionModes(YASC_CRUD_ALL_MODES_V2)
export class CompanyDetailsRes {
    @yascDisplayName('general_backendId')
    @yascFormTooltip('general_backendId')
    @yascTableIgnore(true)
    @yascTableIndex(0)
    @yascFormIgnore()
    public id: number = Number.NaN;

    @yascDisplayName('general_name')
    @yascTableWidth(2)
    @yascTableIndex(1)
    @yascRequired()
    @yascMinLength(2)
    @yascMaxLength(100)
    @yascTableFilterType(FilterType.text)
    @yascFormGrid({width: 1})
    public name: string = "";

    @yascDisplayName('general_description')
    @yascFormKind('textarea')
    @yascFormRows(4)
    @yascMaxLength(2000)
    @yascFormGrid({fullRow: true})
    public description: string = "";

    // Internal timezone offset (UTC+n) — managed programmatically, not user-editable here.
    @yascTableIgnore(true)
    @yascFormIgnore()
    public timezone!: number;

    // Assigned by the system on creation; displayed read-only where needed.
    @yascDisplayName('general_customerNumber')
    @yascTableIgnore(true)
    @yascFormIgnore()
    @yascMaxLength(50)
    public customerNumber: string = "";

    // German Steuernummer — optional, validated as max 20 chars and digits/slash.
    @yascDisplayName('companyTaxNumber')
    @yascTableIgnore(true)
    @yascMaxLength(20)
    @yascPattern(/^[\d/\s-]*$/)
    public stNr: string = "";

    // EU VAT identification number (DE123456789 format).
    @yascDisplayName('companySalesTaxNumber')
    @yascTableIgnore(true)
    @yascMaxLength(20)
    @yascPattern(/^[A-Z]{2}[A-Z0-9]{2,12}$/)
    public ustNr: string = "";

    @yascDisplayName('general_website')
    @yascUrl()
    @yascMaxLength(255)
    @yascFormPlaceholder('https://www.company.com')
    public website: string = "";

    @yascDisplayName('general_email')
    @yascTableWidth(2)
    @yascTableIndex(7)
    @yascEmail()
    @yascMaxLength(255)
    @yascFormPlaceholder('info@company.com')
    @yascTableFilterType(FilterType.text)
    public email: string = "";

    @yascDisplayName('general_phone')
    @yascPhone()
    @yascMaxLength(30)
    @yascFormPlaceholder('+49 123 456789')
    @yascFormTooltip('general_phone_hint')
    @yascTableFilterType(FilterType.text)
    public phone: string = "";

    @yascTableIgnore(true)
    @yascFormIgnore()
    public billingPaymentTermsDays?: number | null;

    @yascTableIgnore(true)
    @yascFormIgnore()
    public billingPaymentDiscountPercent?: number | null;

    @yascTableIgnore(true)
    @yascFormIgnore()
    public billingPaymentDiscountDays?: number | null;

    @yascTableIgnore(true)
    @yascFormIgnore()
    public billingPreferredInvoiceFormat?: InvoiceDocumentFormat | null;
    public billingEdifactInterchangeId?: string | null;
    public billingEdifactCompanyReference?: string | null;

    // Accounting / financial settings — managed in a dedicated settings section, not here.
    @yascTableIgnore(true)
    @yascFormIgnore()
    public typeOfProfitDetermination!: TypeOfProfitDetermination;

    @yascTableIgnore(true)
    @yascFormIgnore()
    public standardFormOfAccounts!: StandardFormOfAccounts;

    // Logo managed via a dedicated file-upload component, not a plain text field.
    @Type(() => FileUploadRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    public logo?: FileUploadRes;

    // Virtual flat fields — values come from the draft (courtOfficeName / taxOfficeName),
    // which toCreateDraft / toUpdateDraft populate from the nested response objects.
    @yascDisplayName('companyCourtOffice')
    @yascTableIgnore(true)
    @yascMaxLength(100)
    public courtOfficeName?: string;

    @yascDisplayName('companyCourtOfficeNr')
    @yascTableIgnore(true)
    @yascMaxLength(64)
    public courtOfficeNr?: string;

    @yascDisplayName('companyTaxOffice')
    @yascTableIgnore(true)
    @yascMaxLength(100)
    public taxOfficeName?: string;

    // Mapped to flat courtOfficeName / taxOfficeName strings on the request type.
    @Type(() => TaxOfficeDetailsRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    public taxOffice: TaxOfficeDetailsRes = new TaxOfficeDetailsRes();

    @Type(() => CourtOfficeDetailsRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    public courtOffice: CourtOfficeDetailsRes = new CourtOfficeDetailsRes();

    // Collections managed by separate feature components, not the main drawer form.
    @Type(() => OpeningTimeDetailRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    public openingTimes: OpeningTimeDetailRes[] = [];

    @Type(() => TrackerDetailRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    public trackers: TrackerDetailRes[] = [];

    @Type(() => NoteDetailRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    public communications: NoteDetailRes[] = [];

    @Type(() => UserDetailsRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    public managingDirectors: UserDetailsRes[] = [];

    @Type(() => AddressDetailRes)
    @yascNested(AddressDetailRes)
    @yascDisplayName('crm_company_addressInfo_tabLabel')
    @yascTableIgnore(true)
    @yascCollection({
        createItem: () => new AddressReq(),
        getItemId: (item) => item.id,
        modes: YASC_CRUD_PERSISTED_MODES_V2
    })
    public addresses: AddressDetailRes[] = [];

    @Type(() => BankAccountRes)
    @yascNested(BankAccountRes)
    @yascDisplayName('crm_company_bankAccounts_tabLabel')
    @yascTableIgnore(true)
    @yascCollection({
        createItem: () => new BankAccountRes(),
        itemIdKey: 'guid',
        modes: YASC_CRUD_PERSISTED_MODES_V2
    })
    public bankAccounts: BankAccountRes[] = [];

    @Type(() => CompanyDepartmentRes)
    @yascTableIgnore(true)
    @yascFormIgnore()
    public departments: CompanyDepartmentRes[] = [];

    @Type(() => ContactPersonDetailRes)
    @yascNested(ContactPersonDetailRes)
    @yascDisplayName('crm_company_resource_tabLabel')
    @yascTableIgnore(true)
    @yascCollection({
        readOnly: true,
        linkHref: '/crm/contacts',
        linkLabel: 'crm_company_toResources_button',
        modes: YASC_CRUD_PERSISTED_MODES_V2
    })
    public contactPerson: ContactPersonDetailRes[] = [];

    public toCreateReq(): CompanyCreateReq {
        return new CompanyCreateReq(this)
    }

    get currentLocalTime(): any {
        let date: Date = new Date(Date.now())

        if (this.timezone != undefined) {
            let offset = new Date(Date.now()).getTimezoneOffset()
            date.setHours(date.getHours() + (offset / 60) + this.timezone)
        }
        return date
    }

    public updateTime() {
        this.currentLocalTime
    }

    get isTimeOpen(): boolean {
        let cDate: Date = new Date(this.currentLocalTime)
        if (this.openingTimes.some(x => x.day === cDate.getDay()) || this.openingTimes.length < 0) {
            let currentTime: number = +(cDate.getHours() + "." + cDate.getMinutes());
            return currentTime > this.openingTimes[cDate.getDay()].from && currentTime < this.openingTimes[cDate.getDay()].to
        }
        return true
    }

    get openingToday(): OpeningTimeDetailRes {
        return this.openingTimes[this.currentLocalTime.getDay()]
    }
}

export class CompanyDepartmentRes {
    guid!: string;
    name!: string;

    private _created: any;
    get created(): any {
        return this._created;
    }

    set created(value: any) {
        this._created = moment(value);
    }
}

export class DepartmentUpdateReq {
    name!: string;
    guid?: string;
}

export class CompanyCreateReq {
    public name!: string;
    public customerNumber?: string;
    public email?: string;
    public phone!: string;
    public website!: string;
    public description!: string;
    public courtOfficeName?: string;
    public courtOfficeNr?: string;
    public taxOfficeName?: string;
    public stNr?: string;
    public ustNr?: string;
    public billingPaymentTermsDays?: number | null;
    public billingPaymentDiscountPercent?: number | null;
    public billingPaymentDiscountDays?: number | null;
    public billingPreferredInvoiceFormat?: InvoiceDocumentFormat | null;
    public billingEdifactInterchangeId?: string | null;
    public billingEdifactCompanyReference?: string | null;

    public bankAccounts: BankAccountRes[] = [];
    public addresses: AddressReq[] = [];
    public departments: DepartmentUpdateReq[] = []
    public contactPerson: ContactPersonUpdateReq[] = [];
    public managingDirectors: number[] = [];
    public logoId?: string;
    timezone?: number = ((new Date(Date.now()).getTimezoneOffset()) / 60) * -1

    constructor(company?: CompanyDetailsRes) {
        if (company !== undefined) {
            this.name = company.name;
            this.customerNumber = company.customerNumber?.trim() || undefined;
            this.website = company.website;
            this.bankAccounts = company.bankAccounts;
            this.addresses = company.addresses;
            this.email = company.email;
            this.description = company.description;
            this.managingDirectors = company.managingDirectors.map(e => e.id);
            this.logoId = company.logo?.id;
            this.phone = company.phone;
            this.contactPerson = company.contactPerson.map(e => new ContactPersonUpdateReq(e));
            this.courtOfficeName = company.courtOfficeName ?? company.courtOffice?.name;
            this.courtOfficeNr = company.courtOfficeNr ?? company.courtOffice?.nr;
            this.taxOfficeName = company.taxOfficeName ?? company.taxOffice?.name;
            this.stNr = company.stNr;
            this.ustNr = company.ustNr;
            this.billingPaymentTermsDays = company.billingPaymentTermsDays ?? null;
            this.billingPaymentDiscountPercent = company.billingPaymentDiscountPercent ?? null;
            this.billingPaymentDiscountDays = company.billingPaymentDiscountDays ?? null;
            this.billingPreferredInvoiceFormat = company.billingPreferredInvoiceFormat ?? null;
            this.billingEdifactInterchangeId = company.billingEdifactInterchangeId ?? null;
            this.billingEdifactCompanyReference = company.billingEdifactCompanyReference ?? null;
        }
    }
}

export class CompanyUpdateReq extends CompanyCreateReq {
    public id!: number

    constructor(company?: CompanyDetailsRes) {
        super(company);
        if (company !== undefined) {
            this.id = company.id;
        }
    }
}


export class BulkCompanyCreateReq {
    companies!: CompanyCreateReq[];

    constructor(companies: CompanyCreateReq[]) {
        this.companies = companies
    }
}

// TODO: implement
export class OnboardRes {
    @Type(() => CompanyDetailsRes)
    company!: CompanyDetailsRes;

    @Type(() => UserDetailsMinRes)
    user!: UserDetailsMinRes;

    token!: string;
}

export class OnboardReq {
    company: OnboardCompanyReq = new OnboardCompanyReq()
    user: OnboardUserReq = new OnboardUserReq();
}


export class OnboardCompanyReq {
    address: AddressReq = new AddressReq();
    name: string = "";
    description?: string = "";
}

export class OnboardUserReq {
    givenName: string = "";
    lastName: string = "";
    title: string = "";
    email: string = "";
    salutation?: number;
    password: string = "";
    confirmPassword: string = "";
    department: string = "";
}

export class BookedProductsCollectionRes {
    @Type(() => BookedProductDetailsRes)
    data: BookedProductDetailsRes[] = [];
    entries: number = 0;
}

export class UserBookedProductRes {
    @Type(() => UserDetailsMinRes)
    user!: UserDetailsMinRes;
    licenseId!: number;

    @Type(() => BookedProductDetailsRes)
    bookedProduct!: BookedProductDetailsRes;
}


export class BookedProductDetailsRes extends ProductDetailsRes {
    autoRenewEnabled!: boolean;
    licenseKey!: string;
    leftActivations!: number;
    _validTil: any;
    _bookedOn: any;
    active!: boolean;
    licenseAmount!: number;

    @Type(() => UserBookedProductRes)
    activatedByUser: UserBookedProductRes[] = [];

    @Type(() => UserDetailsMinRes)
    unassignedByUser: UserDetailsMinRes[] = [];

    isDemo!: boolean;


    get validTil(): any {
        return this._validTil;
    }

    set validTil(value: any) {
        this._validTil = value;
    }

    get bookedOn(): any {
        return this._bookedOn;
    }

    set bookedOn(value: any) {
        this._bookedOn = moment(value);
    }
}

export type OAuthProviderKey = 'GoogleCalendar' | 'Microsoft365';

export class CompanyOAuthSettingsRes {
    providerKey!: OAuthProviderKey;
    isActive!: boolean;
    allowUserOverride!: boolean;
    tenantIdOrHint!: string;
    clientId!: string;
    redirectUri!: string;
    hasClientSecret!: boolean;
}

export interface CompanyOAuthSettingsReq {
    providerKey: OAuthProviderKey;
    isActive: boolean;
    allowUserOverride: boolean;
    tenantIdOrHint: string;
    clientId: string;
    clientSecret: string;
    redirectUri: string;
}

export interface CompanyOAuthSettingsVm extends CompanyOAuthSettingsRes {
    clientSecret: string;
}